// A project's page (/projects/<code>): what it is, set up once (Mitch, 2026-11-04: a project is set up once, the work is
// on its runs). One header row (its basics, readiness and runs), then one list down the left and the step picked
// beside it, no tabs (Mitch: "get rid of the little tab menus for that entirely, so the changes option is on the left
// menu with the setup options, with a separator, so it's always visible"): the setup steps (?tab= names the step: none is
// Basics, then products, build, sourcing, files, costs, production), a line, then Changes (?tab=changes). Runs live under
// Runs (/runs/PR-001); the header's "3 runs · 1 open" opens the project's, and old links (?tab=productions&run=) go
// there. An item or a part's spec opens in the side pane (?open=<item id>, ?spec=<item id>). Every page takes the whole
// width. Each step is built from ui/PageParts.tsx: a strip of figures, then plain sections, each one table or one box.
// Everything is from synced rows through shared/projects.ts, worked out once per change (useProjectIndex), so steps
// switch instantly.
import { type ComponentProps, type ReactNode, useEffect } from "react";
import type { Project } from "../../../../shared/db.gen.ts";
import {
  type ProjectIndex,
  type ProjectScope,
  projectScope,
  type Readiness,
  readiness,
  STEP_STATUS_LABELS,
  type Step,
  type StepKey,
  type StepStatus,
} from "../../../../shared/projects.ts";
import { runPath } from "../../../../shared/runs.ts";
import { cost2 } from "../../lib/boms.ts";
import { money, options, PROJECT_STATUS_LABELS } from "../../lib/format.ts";
import { can } from "../../lib/mutate.ts";
import { gapLink, useProjectIndex } from "../../lib/projects.ts";
import { Link, navigate, setSearch, usePath, useSearch } from "../../lib/router.tsx";
import { useRows } from "../../lib/sync.ts";
import { Badge, cx } from "../../ui/controls.tsx";
import { Figure, Figures, PageHeader, PageSection } from "../../ui/PageParts.tsx";
import { Fields } from "../../ui/RecordPanel.tsx";
import { PaneLink, usePaneParam } from "../../ui/SidePane.tsx";
import { BomEditor } from "../BomEditor.tsx"; // a BOM opens in the Build tab, keeping the project
import { BuildTab } from "./BuildTab.tsx";
import { CostsTab } from "./CostsTab.tsx"; // Production runs --- the cost stack, waterfall, what-if, tooling
import { FilesTab, NO_PARTS, SourcingTab } from "./PartsTabs.tsx";
import { ChangesTab, ProductionTab } from "./ProductionTab.tsx";
import { ProductsTab } from "./ProductsTab.tsx";
import { PROJECT_STATUS_TONE, ReadinessBar, StatusIcon } from "./ProjectsPage.tsx";

export const TABS = ["overview", "products", "build", "sourcing", "files", "costs", "production", "changes"] as const;
export type Tab = (typeof TABS)[number];
const TAB_LABELS: Record<Tab, string> = {
  overview: "Basics",
  products: "Products",
  build: "Build",
  sourcing: "Sourcing",
  files: "Specs & files",
  costs: "Costs",
  production: "CM",
  changes: "Changes",
};
/** The setup steps, in order, each a page beside the list; Changes comes after them, below a line. */
const STEPS = ["overview", "products", "build", "sourcing", "files", "costs", "production"] as const satisfies readonly Tab[];
/** What leaving a step clears: a BOM open in Build, the Product flow's picks. */
const RESET = { open: null, item: null, build: null, bom: null, btab: null, variant: null } as const;
/** The setup steps each page shows (Build covers the sub-assemblies too). */
const TAB_STEPS: Partial<Record<Tab, StepKey[]>> = {
  overview: ["basics"],
  products: ["products"],
  build: ["build", "subassemblies"],
  sourcing: ["sourcing"],
  files: ["files"],
  costs: ["costs"],
  production: ["production"],
};
const RANK: Record<StepStatus, number> = { needs_work: 0, not_started: 1, done: 2 };
const worstOf = (steps: Step[]) => steps.reduce<StepStatus>((w, s) => (RANK[s.status] < RANK[w] ? s.status : w), "done");

export type TabProps = { project: Project; px: ProjectIndex; scope: ProjectScope; r: Readiness; here: string };

export function Workspace({ project }: { project: Project }) {
  const px = useProjectIndex();
  const path = usePath();
  const params = useSearch();
  const tab = (TABS as readonly string[]).includes(params.get("tab") ?? "") ? (params.get("tab") as Tab) : "overview";
  // Side pane --- an item opens in the side pane: every step's ?open=<item id> (a gap's link, a part's row) lands there
  usePaneParam("open", "item");
  usePaneParam("spec", "spec");
  // Runs moved to /runs: an old link to the project's Runs tab, or a run in it, goes there
  const oldRuns = params.get("tab") === "productions";
  const oldRun = oldRuns ? params.get("run") : null;
  const oldRt = oldRuns ? params.get("rt") : null;
  useEffect(() => {
    if (!oldRuns) return;
    navigate(oldRun ? runPath({ number: oldRun }, oldRt ?? undefined) : `/runs?project=${encodeURIComponent(project.code || project.id)}`, { replace: true });
  }, [oldRuns, oldRun, oldRt, project]);
  const bom = tab === "build" ? params.get("bom") : null; // a BOM open in Build (lib/boms.ts bomPlaceFor)
  const scope = projectScope(px, project.id);
  const r = readiness(px, project.id);
  const here = `${path}${tab === "overview" ? "" : `?tab=${tab}`}`;

  if (!scope || !r || oldRuns) return null;
  const props: TabProps = { project, px, scope, r, here };
  return (
    <div className="flex h-full min-h-0 flex-col">
      <Header project={project} r={r} px={px} />
      <div className="flex min-h-0 flex-1 bg-bg">
        {/* A BOM open in Build is a level deeper: it takes the whole width, with its own way back */}
        {!bom && <StepList r={r} step={tab} />}
        <div className={cx("min-w-0 flex-1", bom ? "overflow-hidden" : "overflow-y-auto")}>
          {tab === "overview" && <BasicsStep {...props} />}
          {tab === "products" && <ProductsTab {...props} />}
          {tab === "build" && (bom ? <BomEditor key={bom} id={bom} /> : <BuildTab {...props} />)}
          {tab === "sourcing" && <SourcingTab {...props} />}
          {tab === "files" && <FilesTab {...props} />}
          {tab === "costs" && <CostsTab {...props} />}
          {tab === "production" && <ProductionTab {...props} />}
          {tab === "changes" && <ChangesTab {...props} />}
        </div>
      </div>
    </div>
  );
}

/**
 * The project's list down the left, always in sight: the setup steps, each with its mark (done, needs work, not started),
 * then a line and Changes; a click opens it beside the list.
 */
function StepList({ r, step }: { r: Readiness; step: Tab }) {
  const drafts = r.notices.filter((n) => n.status === "draft").length;
  const item = (t: Tab, mark: ReactNode, title: string, count?: string) => (
    <button
      key={t}
      type="button"
      onClick={() => setSearch({ tab: t === "overview" ? null : t, ...RESET })}
      title={title}
      aria-current={step === t ? "page" : undefined}
      className={cx(
        "mx-2 flex items-center gap-2 rounded px-2 py-1.5 text-left",
        step === t ? "bg-hover font-medium text-text" : "text-muted hover:bg-hover hover:text-text",
      )}
    >
      {mark}
      <span className="min-w-0 truncate">{TAB_LABELS[t]}</span>
      {count && <span className="ml-auto shrink-0 font-mono text-[11px] font-normal text-faint">{count}</span>}
    </button>
  );
  return (
    <nav aria-label="Project" className="flex w-44 shrink-0 flex-col gap-px overflow-y-auto border-r border-line bg-surface py-2 text-[13px]">
      {STEPS.map((t) => {
        const steps = (TAB_STEPS[t] ?? []).map((k) => r.steps.find((s) => s.key === k)).filter((s): s is Step => !!s);
        return item(
          t,
          steps.length > 0 && <StatusIcon status={worstOf(steps)} className="size-3.5 shrink-0" />,
          steps.map((s) => `${s.label}: ${STEP_STATUS_LABELS[s.status]}`).join(" · "),
        );
      })}
      <hr className="mx-4 my-1.5 border-0 border-t border-line" />
      {item(
        "changes",
        <span className="size-3.5 shrink-0" />,
        "The change notices that touch this project's parts and variants",
        r.notices.length ? `${r.notices.length}${drafts ? ` · ${drafts} draft` : ""}` : undefined,
      )}
      {/* Every project's, off the sidebar since BOMs and specs live here (Mitch, 2026-11-04: manufacturing around runs) */}
      <div className="mx-2 mt-auto flex flex-col gap-0.5 border-t border-line px-2 pt-2 text-[12px] text-faint">
        <span>Every project's</span>
        <Link to="/boms" className="text-muted hover:text-text hover:underline">
          BOMs
        </Link>
        <Link to="/specs" className="text-muted hover:text-text hover:underline">
          Specs and change notices
        </Link>
      </div>
    </nav>
  );
}

function Header({ project, r, px }: { project: Project; r: Readiness; px: ProjectIndex }) {
  const users = useRows("app_user");
  const runs = useRows("production_run").filter((x) => x.project_id === project.id);
  const open = runs.filter((x) => x.status !== "closed").length;
  const cm = project.cm_vendor_id ? px.vendorById.get(project.cm_vendor_id) : undefined;
  const owner = users.find((u) => u.id === project.owner_id);
  const none = (text: string) => <span className="text-faint">{text}</span>;
  const facts: [string, ReactNode][] = [
    [
      "CM",
      cm ? (
        <PaneLink kind="vendor" id={cm.id} key="cm" to={`/vendors?open=${cm.id}`}>
          {cm.short_name || cm.name}
        </PaneLink>
      ) : (
        none("not chosen")
      ),
    ],
    ["Launch", project.launch_date ? String(project.launch_date).slice(0, 10) : none("no date")],
    ["Owner", owner ? owner.name || owner.email : none("nobody")],
    [
      "Runs",
      // Its runs are under Runs, a page of their own (Mitch, 2026-11-04: a project is set up once, the work is on its runs)
      <Link key="runs" to={`/runs?project=${encodeURIComponent(project.code || project.id)}`}>
        {runs.length ? `${runs.length}${open ? ` · ${open} open` : ""}` : "none yet"}
      </Link>,
    ],
  ];
  return (
    <PageHeader
      crumbs={[{ label: "Projects", to: "/projects" }]}
      title={project.name}
      actions={
        // Readiness in a word and a bar; Configure has the steps
        <button
          type="button"
          onClick={() => setSearch({ tab: null, ...RESET })}
          title={r.summary}
          className="flex shrink-0 items-center gap-2 rounded px-1.5 py-1 text-[12px] hover:bg-hover"
        >
          <ReadinessBar r={r} className="w-24" />
          <span className="whitespace-nowrap">
            <span className="font-medium">{r.ready}</span>
            <span className="text-muted"> of {r.total} ready</span>
          </span>
        </button>
      }
    >
      {project.code && <span className="font-mono text-muted">{project.code}</span>}
      <Badge tone={PROJECT_STATUS_TONE[project.status]}>{PROJECT_STATUS_LABELS[project.status]}</Badge>
      {facts.map(([label, value]) => (
        <span key={label} className="whitespace-nowrap">
          <span className="text-faint">{label} </span>
          <span className="[&_a]:hover:underline">{value}</span>
        </span>
      ))}
    </PageHeader>
  );
}

/** Configure → Basics: the project's numbers at a glance, what's missing, and its basics (each saves as it's changed). */
function BasicsStep({ project, px, r, here }: TabProps) {
  const vendors = useRows("vendor");
  const users = useRows("app_user");
  const cms = vendors
    .filter((v) => !v.archived || v.id === project.cm_vendor_id)
    .sort((a, b) => Number(a.kind === "supplier") - Number(b.kind === "supplier") || (a.short_name || a.name).localeCompare(b.short_name || b.name));
  return (
    <div className="flex flex-col gap-4 p-4">
      <KeyNumbers r={r} />
      <StepGaps r={r} steps={["basics"]} project={project} px={px} here={here} />
      <PageSection title="Basics" note="Each change saves as you make it" boxed className="max-w-2xl">
        <div className="flex flex-col gap-0.5 p-2">
          <Fields
            row={project}
            update="project.update"
            disabled={!can("project.update")}
            fields={[
              { key: "name", label: "Name" },
              { key: "code", label: "SKU prefix", mono: true, placeholder: "e.g. GCU2" },
              { key: "status", label: "Status", type: "select", options: options(PROJECT_STATUS_LABELS) },
              {
                key: "cm_vendor_id",
                label: "CM",
                type: "select",
                nullable: true,
                options: [{ value: "", label: "Not chosen yet" }, ...cms.map((v) => ({ value: v.id, label: v.short_name || v.name }))],
              },
              { key: "launch_date", label: "Target launch", type: "date" },
              {
                key: "owner_id",
                label: "Owner",
                type: "select",
                nullable: true,
                options: [
                  { value: "", label: "Nobody yet" },
                  ...users
                    .filter((u) => (u.active && u.role !== "receiving") || u.id === project.owner_id)
                    .map((u) => ({ value: u.id, label: u.name || u.email })),
                ],
              },
              { key: "notes", label: "Notes", type: "textarea" },
            ]}
          />
        </div>
      </PageSection>
    </div>
  );
}

/** The project's key numbers, one strip (Figures), above its steps. */
function KeyNumbers({ r }: { r: Readiness }) {
  const n = r.numbers;
  const pct = (x: number) => `${Math.round(x * 100)}%`;
  const range = (x: { min: number; max: number } | null, f: (v: number) => string) => (!x ? "—" : f(x.min) === f(x.max) ? f(x.min) : `${f(x.min)}–${f(x.max)}`);
  const missing = r.costs.some((c) => !c.complete);
  return (
    <Figures>
      <Figure label="Variants" value={String(n.variants)} sub={`${n.products} ${n.products === 1 ? "product" : "products"}`} />
      <Figure
        label="Parts"
        value={String(n.parts)}
        sub={n.parts ? `${n.materials} materials · ${n.subassemblies} sub-assemblies` : undefined}
        title={n.parts ? `${n.materials} materials · ${n.subassemblies} sub-assemblies · ${n.services} services` : NO_PARTS}
      />
      <Figure label="BOMs" value={String(n.builds)} sub={n.builds ? `${n.builds - n.drafts} released · ${n.drafts} draft` : undefined} />
      <Figure label="Specs released" value={`${n.specsReleased} of ${n.specsNeeded}`} />
      <Figure
        label="Cost per unit"
        value={range(n.cost, (v) => cost2(v))}
        sub={missing ? "some missing" : undefined}
        tone={missing ? "warn" : undefined}
        title={missing ? "Some variants are missing costs" : undefined}
      />
      <Figure label="Margin" value={range(n.margin, pct)} title="The roll-up cost against the Shopify price" />
      <Figure
        label="Consigned at the CM"
        value={money(Math.round(n.consignedValue))}
        sub={n.consignedMissing ? `${n.consignedMissing} without a cost` : undefined}
        tone={n.consignedMissing ? "warn" : undefined}
      />
      <Figure label="Make orders" value={String(n.makeOrders)} sub={n.makeOrders ? `${n.openMakeOrders} open` : undefined} />
    </Figures>
  );
}

/** A section of a tab: its heading, then its content (ui/PageParts.tsx `PageSection`; `boxed` for what isn't a table). */
export function Card(props: ComponentProps<typeof PageSection>) {
  return <PageSection {...props} boxed={props.boxed ?? true} />;
}

/** How many of a gap's items a step's strip names (the rest: "N more"). */
const GAP_NAMES = 6;

/** The gaps of a tab's steps, above its list, each naming what it's about. */
export function StepGaps({ r, steps, project, px, here }: { r: Readiness; steps: StepKey[]; project: Project; px: ProjectIndex; here: string }) {
  const shown = r.steps.filter((s) => steps.includes(s.key));
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-1">
      {shown.map((s) => (
        // The step and its status stay on one line; what's missing wraps after them
        <span key={s.key} className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 text-[12px]">
          <StatusIcon status={s.status} className="size-3.5 shrink-0 self-center" />
          <span className="font-medium whitespace-nowrap">{s.label}</span>
          <span className="whitespace-nowrap text-muted">{STEP_STATUS_LABELS[s.status]}</span>
          {s.gaps
            .filter((g) => !g.optional)
            .map((g) => (
              <span key={g.key} className="text-muted">
                ·{" "}
                {g.target ? (
                  <Link to={gapLink(px, project, g.target, here)} className="hover:underline">
                    {g.text}
                  </Link>
                ) : (
                  g.text
                )}
                {g.items.length > 0 && (
                  <>
                    {": "}
                    {g.items.slice(0, GAP_NAMES).map((i, n) => (
                      <span key={i.id}>
                        {n > 0 && ", "}
                        <Link to={gapLink(px, project, i.target, here)} className="text-text hover:underline" title={i.detail}>
                          {i.sku || i.label}
                        </Link>
                      </span>
                    ))}
                    {g.items.length > GAP_NAMES && <span className="text-faint">{`, ${g.items.length - GAP_NAMES} more`}</span>}
                  </>
                )}
              </span>
            ))}
        </span>
      ))}
    </div>
  );
}
